import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Figma (node 183:417 — Tabs):
 * Variant=Default (pill): List bg-muted, rounded-lg, h-8, p-[3px]
 *   Active trigger: bg-background, rounded-md, shadow-sm, text-foreground
 *   Inactive trigger: text-muted-foreground
 *   Trigger padding: px-1.5 py-1
 * Variant=Line (underline): List flex, gap-1, no background
 *   Active trigger: border-b-2 border-foreground, text-foreground
 *   Inactive trigger: text-muted-foreground
 *   Trigger padding: p-1.5
 * Both: text-sm font-medium, gap-1.5 (icon/text/badge)
 * Orientation: horizontal (default) / vertical (default variant only)
 */

type TabsVariant = 'default' | 'line'

const TabsVariantContext = React.createContext<TabsVariant>('default')

// --- Tabs (Root) ---

const IntuiTabs = TabsPrimitive.Root
IntuiTabs.displayName = 'IntuiTabs'

// --- TabsList ---

const tabsListVariants = cva(
  'inline-flex items-center',
  {
    variants: {
      variant: {
        default:
          'h-8 rounded-lg bg-muted p-[3px] data-[orientation=vertical]:h-auto data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch',
        line: 'gap-1 data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch',
      },
    },
    defaultVariants: { variant: 'default' },
  }
)

export interface IntuiTabsListProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>,
    VariantProps<typeof tabsListVariants> {}

const IntuiTabsList = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.List>,
  IntuiTabsListProps
>(({ className, variant = 'default', ...props }, ref) => (
  <TabsVariantContext.Provider value={variant!}>
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  </TabsVariantContext.Provider>
))
IntuiTabsList.displayName = 'IntuiTabsList'

// --- TabsTrigger ---

const IntuiTabsTrigger = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = React.useContext(TabsVariantContext)
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 whitespace-nowrap text-sm font-medium',
        'text-muted-foreground transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        variant === 'line'
          ? [
              'p-1.5',
              'data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-foreground',
              'data-[orientation=vertical]:data-[state=active]:border-b-0 data-[orientation=vertical]:data-[state=active]:border-l-2',
            ]
          : [
              'h-full px-1.5 py-1 rounded-md',
              'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
            ],
        className
      )}
      {...props}
    />
  )
})
IntuiTabsTrigger.displayName = 'IntuiTabsTrigger'

// --- TabsContent ---

const IntuiTabsContent = React.forwardRef<
  React.ComponentRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className
    )}
    {...props}
  />
))
IntuiTabsContent.displayName = 'IntuiTabsContent'

export {
  IntuiTabs,
  IntuiTabsList,
  IntuiTabsTrigger,
  IntuiTabsContent,
}
